import { Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { sideViewState, useConfig } from '@chainlit/react-client';

import ElementSideView from '@/components/ElementSideView';
import { AnnualProfileWizard } from '@/components/AnnualProfileWizard';
import LeftSidebar from '@/components/LeftSidebar';
import { TaskList } from '@/components/Tasklist';
import { ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';

import { userEnvState } from 'state/user';

type Props = {
  children: JSX.Element;
};

// Renders the sidebar + main content and offsets the main content based on the
// sidebar's desktop open state. Must be a child of SidebarProvider so it can
// read `open` via useSidebar.
const SidebarLayout = ({ children }: Props) => {
  const { open } = useSidebar();

  return (
    <>
      <LeftSidebar />
      <div
        className={`fongpt-page-offset fongpt-mobile-viewport relative flex h-screen w-screen ${
          open ? 'md:pl-[284px]' : 'md:pl-0'
        }`}
      >
        {children}
      </div>
    </>
  );
};

const Page = ({ children }: Props) => {
  const { config } = useConfig();
  const userEnv = useRecoilValue(userEnvState);
  const sideView = useRecoilValue(sideViewState);

  if (config?.userEnv) {
    for (const key of config.userEnv || []) {
      if (!userEnv[key]) return <Navigate to="/env" />;
    }
  }

  const historyEnabled = config?.dataPersistence;

  const mainContent = (
    <div className="fongpt-shell flex h-full w-full flex-col overflow-hidden relative">
      <ResizablePanelGroup
        direction="horizontal"
        className="flex flex-row flex-grow relative z-10"
      >
        <ResizablePanel
          className="relative flex flex-col h-full w-full"
          minSize={40}
          defaultSize={60}
        >
          <AnnualProfileWizard />
          <div className="flex h-full min-h-0 w-full flex-row overflow-hidden">
            {children}
          </div>
        </ResizablePanel>
        {sideView ? <ElementSideView /> : <TaskList isMobile={false} />}
      </ResizablePanelGroup>
    </div>
  );

  return (
    <SidebarProvider
      defaultOpen={config?.ui.default_sidebar_state !== 'closed'}
    >
      {historyEnabled ? (
        <SidebarLayout>{mainContent}</SidebarLayout>
      ) : (
        <div className="fongpt-mobile-viewport h-screen w-screen flex">
          {mainContent}
        </div>
      )}
    </SidebarProvider>
  );
};

export default Page;
